import { Slot } from '@radix-ui/react-slot';
import type { VariantProps } from 'class-variance-authority';
import * as React from 'react';

import { cn } from '@heidi/utils';
import { Separator } from '../separator';
import { typographyVariant } from '../typography';

import { itemMediaVariants, itemVariants } from './item.styles';
import type {
  ItemActionsProps,
  ItemContentProps,
  ItemDescriptionProps,
  ItemFooterProps,
  ItemGroupProps,
  ItemHeaderProps,
  ItemMediaProps,
  ItemProps,
  ItemSeparatorProps,
  ItemTitleProps,
} from './item.types';

type ItemSize = NonNullable<VariantProps<typeof itemVariants>['size']>;

const ItemSizeContext = React.createContext<ItemSize>('default');

const ItemGroup = React.forwardRef<HTMLDivElement, ItemGroupProps>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      role="list"
      data-slot="item-group"
      className={cn(
        'group/item-group flex w-full flex-col gap-4 has-[[data-size=sm]]:gap-2',
        className
      )}
      {...props}
    />
  )
);
ItemGroup.displayName = 'ItemGroup';

const ItemSeparator = React.forwardRef<
  React.ElementRef<typeof Separator>,
  ItemSeparatorProps
>(({ className, orientation = 'horizontal', ...props }, ref) => (
  <Separator
    ref={ref}
    data-slot="item-separator"
    orientation={orientation}
    className={cn('my-2', className)}
    {...props}
  />
));
ItemSeparator.displayName = 'ItemSeparator';

const Item = React.forwardRef<HTMLDivElement, ItemProps>(
  (
    { className, variant = 'default', size = 'default', asChild = false, ...props },
    ref
  ) => {
    const Comp = asChild ? Slot : 'div';
    const resolvedSize: ItemSize = size ?? 'default';
    return (
      <ItemSizeContext.Provider value={resolvedSize}>
        <Comp
          ref={ref}
          data-slot="item"
          data-variant={variant}
          data-size={resolvedSize}
          className={cn(itemVariants({ variant, size: resolvedSize }), className)}
          {...props}
        />
      </ItemSizeContext.Provider>
    );
  }
);
Item.displayName = 'Item';

const ItemMedia = React.forwardRef<HTMLDivElement, ItemMediaProps>(
  ({ className, variant = 'default', ...props }, ref) => (
    <div
      ref={ref}
      data-slot="item-media"
      data-variant={variant}
      className={cn(itemMediaVariants({ variant }), className)}
      {...props}
    />
  )
);
ItemMedia.displayName = 'ItemMedia';

const ItemContent = React.forwardRef<HTMLDivElement, ItemContentProps>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      data-slot="item-content"
      className={cn(
        'flex flex-1 flex-col gap-1 group-data-[size=sm]/item:gap-0 [&+[data-slot=item-content]]:flex-none',
        className
      )}
      {...props}
    />
  )
);
ItemContent.displayName = 'ItemContent';

const ItemTitle = React.forwardRef<HTMLDivElement, ItemTitleProps>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      data-slot="item-title"
      className={cn(
        typographyVariant({ variant: 'p2Bold' }),
        // `text-start` guards against inheriting a centred `text-align` from an
        // interactive ancestor — `Item asChild` renders a native `<button>`
        // (UA default `text-align: center`), which otherwise centres a wrapped
        // title's short line under its long one while sibling rows stay
        // left-aligned. `ItemDescription` sets the same guard.
        'line-clamp-1 flex w-fit items-center gap-2 text-start underline-offset-4',
        className
      )}
      {...props}
    />
  )
);
ItemTitle.displayName = 'ItemTitle';

const ItemDescription = React.forwardRef<HTMLParagraphElement, ItemDescriptionProps>(
  ({ className, ...props }, ref) => {
    const size = React.useContext(ItemSizeContext);

    const variant = size === 'sm' ? 'caption' : 'p2';
    return (
      <p
        ref={ref}
        data-slot="item-description"
        className={cn(
          typographyVariant({ variant }),
          'line-clamp-2 text-start text-text-secondary [&>a:hover]:text-text-link-hover [&>a]:underline [&>a]:underline-offset-4',
          className
        )}
        {...props}
      />
    );
  }
);
ItemDescription.displayName = 'ItemDescription';

const ItemActions = React.forwardRef<HTMLDivElement, ItemActionsProps>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      data-slot="item-actions"
      className={cn('flex items-center gap-2', className)}
      {...props}
    />
  )
);
ItemActions.displayName = 'ItemActions';

const ItemHeader = React.forwardRef<HTMLDivElement, ItemHeaderProps>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      data-slot="item-header"
      className={cn('flex basis-full items-center justify-between gap-2', className)}
      {...props}
    />
  )
);
ItemHeader.displayName = 'ItemHeader';

const ItemFooter = React.forwardRef<HTMLDivElement, ItemFooterProps>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      data-slot="item-footer"
      className={cn('flex basis-full items-center justify-between gap-2', className)}
      {...props}
    />
  )
);
ItemFooter.displayName = 'ItemFooter';

export {
  Item,
  ItemActions,
  ItemContent,
  ItemDescription,
  ItemFooter,
  ItemGroup,
  ItemHeader,
  ItemMedia,
  ItemSeparator,
  ItemTitle,
};
